/*----------------------------------
- PAYMENT: PRODUCT URLS
----------------------------------*/

import { appErrorReporter } from '@/common/libs/errorReporting';
/*----------------------------------
- CONSTANTS
----------------------------------*/

const PRODUCT_LOCAL_PORT = '3020';
export const PRODUCT_PRODUCTION_ORIGIN = 'https://app.unique.domains';
const ABSOLUTE_URL_PATTERN = /^[a-zA-Z][a-zA-Z\d+\-.]*:/;

/*----------------------------------
- HELPERS
----------------------------------*/

const isLocalHostname = (hostname: string): boolean => {
    return hostname === 'localhost' || hostname === '127.0.0.1';
};

const resolveServerEnvName = (): string | undefined => {
    const processValue = Reflect.get(globalThis, 'process');
    if (!processValue || typeof processValue !== 'object') return undefined;

    const envValue = Reflect.get(processValue, 'env');
    if (!envValue || typeof envValue !== 'object') return undefined;

    const envName = Reflect.get(envValue, 'ENV_NAME');
    return typeof envName === 'string' ? envName : undefined;
};

const readServerEnvValue = (key: string): string | null => {
    const processValue = Reflect.get(globalThis, 'process');
    if (!processValue || typeof processValue !== 'object') return null;

    const envValue = Reflect.get(processValue, 'env');
    if (!envValue || typeof envValue !== 'object') return null;

    const rawValue = Reflect.get(envValue, key);
    return typeof rawValue === 'string' && rawValue.trim() ? rawValue.trim() : null;
};

const toOrigin = (value: string | null): string | null => {
    if (!value) return null;

    try {
        return new URL(value).origin;
    } catch (urlError) {
        appErrorReporter.app.handleError(urlError);
        console.warn('[payment-product-urls] Failed to parse product origin.', urlError);
        return null;
    }
};

const resolveServerProductOrigin = (): string => {
    const publicOrigin =
        toOrigin(readServerEnvValue('PRODUCT_PUBLIC_URL')) ||
        toOrigin(readServerEnvValue('PRODUCT_URL')) ||
        (resolveServerEnvName() === 'local' ? toOrigin(readServerEnvValue('PRODUCT_URL_INTERNAL')) : null);
    if (publicOrigin) return publicOrigin;

    return resolveServerEnvName() === 'local'
        ? `http://localhost:${PRODUCT_LOCAL_PORT}`
        : PRODUCT_PRODUCTION_ORIGIN;
};

const resolveProductOrigin = (productOrigin?: string | null): string => {
    const explicitOrigin = toOrigin(productOrigin || null);
    if (explicitOrigin) return explicitOrigin;

    if (typeof window === 'undefined') {
        return resolveServerProductOrigin();
    }

    const { hostname, protocol } = window.location;
    if (isLocalHostname(hostname)) {
        return `${protocol}//${hostname}:${PRODUCT_LOCAL_PORT}`;
    }

    if (hostname === 'app.unique.domains') {
        return `${protocol}//${hostname}`;
    }

    return PRODUCT_PRODUCTION_ORIGIN;
};

/*----------------------------------
- PUBLIC API
----------------------------------*/

export const toAbsoluteProductUrl = (href: string): string => {
    const normalizedHref = href.trim();
    if (!normalizedHref) return href;
    if (ABSOLUTE_URL_PATTERN.test(normalizedHref)) return normalizedHref;

    return new URL(normalizedHref, `${resolveProductOrigin()}/`).toString();
};

export const toAbsoluteProductUrlWithOrigin = (href: string, productOrigin?: string | null): string => {
    const normalizedHref = href.trim();
    if (!normalizedHref) return href;
    if (ABSOLUTE_URL_PATTERN.test(normalizedHref)) return normalizedHref;

    return new URL(normalizedHref, `${resolveProductOrigin(productOrigin)}/`).toString();
};
